import React, { useState, ChangeEvent } from "react";
import { Button, Form } from "react-bootstrap";
import useFetch from "../../../../hooks/useFetch";
import { getAPIHeaders, getAPIMultiPartHeaders } from "../../../../common/Utils";
import { API } from "../../../../common/Constants";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import PreLoader from "../../../../core/common/preLoader";
import CustomModal from "../../../../core/common/customModal";

interface AssessmentUploadModalProps {
  show: boolean;
  onHide: () => void;
  assessmentId: string;
  onSuccess?: () => void;
}

const AssessmentUploadModal: React.FC<AssessmentUploadModalProps> = ({ 
  show, 
  onHide, 
  assessmentId,
  onSuccess 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState<boolean>(true);
  const [instructionsExpanded, setInstructionsExpanded] = useState<boolean>(false);

  const { callFetch } = useFetch();

  // Reset the state when modal is shown/hidden
  const resetModal = () => {
    setFile(null);
    setUploadResult(null);
    setError(null);
    setShowInstructions(true);
    setInstructionsExpanded(false);
  };

  // Handle file selection
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setError(null);
      setUploadResult(null);
      setShowInstructions(false);
    }
  };

  // Handle template download
  const handleDownloadTemplate = async () => {
    setIsLoadingTemplate(true);
    setError(null);

    try {
      const response = await callFetch(
        API.ASSESSMENT_IMPORT_TEMPLATE,
        "GET",
        getAPIHeaders(),
        null,
        null
      );

      if (response) {
        // Use the CloudFlare R2 link to download the file
        const link = document.createElement('a');
        link.href = response;
        link.download = `assessment-questions-template.xlsx`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('No download URL received');
      }
    } catch (err: any) {
      console.error("Template download error:", err);
      setError(err?.response?.data || "Failed to download template. Please try again.");
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file to upload");
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await callFetch(
        API.ASSESSMENT_IMPORT.replace(':assessmentId', assessmentId),
        'POST',
        getAPIMultiPartHeaders(),
        null,
        formData
      );

      setUploadResult(response);
      setIsUploading(false);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error("Upload error:", err);
      setError(err?.response?.data || "Failed to upload questions. Please try again.");
      setIsUploading(false);
    }
  };

  // Handle upload another file
  const handleUploadAnother = () => {
    resetModal();
  };

  return (
    <CustomModal 
      show={show} 
      title="Upload Assessment Questions" 
      onClose={() => {
        resetModal();
        onHide();
      }}
      size="lg"
      headerContent={
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-3"
          onClick={handleDownloadTemplate}
          disabled={isLoadingTemplate}
        >
          {isLoadingTemplate ? (
            <>
              <i className="fa fa-spinner fa-spin me-1"></i> Downloading...
            </>
          ) : (
            <>
              <i className="fa fa-download me-1"></i> Download Template
            </>
          )}
        </Button>
      }
      bodyContent={
        <>
          {(isUploading || isLoadingTemplate) && <PreLoader showLoader={true} />}

          {showInstructions && !file && (
            <div className="alert alert-info">
              <div 
                className="d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => setInstructionsExpanded(!instructionsExpanded)}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="alert-heading mb-0">
                  <i className="fa fa-info-circle me-2"></i>
                  Instructions for Uploading Questions
                </h6>
                <i className={`fa fa-chevron-${instructionsExpanded ? 'up' : 'down'}`}></i>
              </div>
              {instructionsExpanded && (
                <>
                  <hr />
                  <ol className="mb-0">
                    <li className="mb-2">
                      <strong>Download the template</strong> by clicking "Download Template" button.
                    </li>
                    <li className="mb-2">
                      <strong>Open the downloaded template</strong> to add questions. Choose any below options to add questions:
                      <ul className="mt-1 mb-0">
                        <li className="mb-1">
                          <strong>a) Add question using MCQ Form</strong>
                          <ul className="mt-1 mb-0">
                            <li>Click on "MCQ Form" sheet</li>
                            <li>Use the form to enter question text, options, and correct option(s)</li>
                            <li>For correct option, provide option name. For example if correct option is 'a' just specify 'a'. If there are multiple correct options then add comma separated options eg. 'a,b,c'</li>
                          </ul>
                        </li>
                        <li className="mb-1">
                          <strong>b) Add questions manually</strong>
                          <ul className="mt-1 mb-0">
                            <li>Click on "Import Data" sheet</li>
                            <li>Enter question text, options, and correct option(s)</li>
                            <li>For correct option, provide option name. For example if correct option is 'a' just specify 'a'. If there are multiple correct options then add comma separated options eg. 'a,b,c'</li>
                          </ul>
                        </li>
                      </ul>
                    </li>
                    <li className="mb-2">
                      <strong>Click on "Browse Files"</strong> button to upload the edited file.
                    </li>
                    <li className="mb-0">
                      <strong>Click on "Upload Questions"</strong> button to upload the questions.
                    </li>
                  </ol>
                </>
              )}
            </div>
          )}

          {!uploadResult ? (
            <>
              <Form.Group className="mb-4">
                <Form.Label>Upload Questions File</Form.Label>
                <div className="file-upload-container border rounded p-3">
                  <div className="d-flex flex-column align-items-center">
                    {file ? (
                      <div className="d-flex align-items-center w-100">
                        <ImageWithBasePath
                          src="assets/img/icons/xls.svg"
                          alt="Excel"
                          className="me-3 excel-icon"
                        />
                        <div className="flex-grow-1">
                          <p className="mb-0 fw-medium text-primary">
                            {file.name}
                          </p>
                          <small className="text-muted">
                            {(file.size / 1024).toFixed(2)} KB
                          </small>
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setFile(null)}
                          disabled={isUploading}
                        >
                          Change
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="text-center mb-3">
                          <ImageWithBasePath
                            src="assets/img/icons/xls.svg"
                            alt="Upload"
                            className="upload-icon"
                          />
                          <h6 className="mt-2 mb-1">Select Questions File</h6>
                          <p className="text-muted small mb-3">
                            Supported formats: .xlsx, .xls, .xlsm, .csv
                          </p>
                        </div>

                        <div className="w-100 d-flex justify-content-center">
                          <label className="btn btn-outline-primary">
                            Browse Files
                            <Form.Control
                              type="file"
                              accept=".xlsx,.xls,.xlsm,.csv"
                              onChange={handleFileChange}
                              disabled={isUploading}
                              style={{ display: "none" }}
                            />
                          </label>
                        </div>
                      </>
                    )}
                  </div>

                  {file && (
                    <div className="mt-3 border-top pt-3">
                      <div className="d-flex align-items-center">
                        <div className="text-success me-2">
                          <i className="fa fa-check-circle"></i>
                        </div>
                        <div className="text-muted small">
                          File selected and ready for upload
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Form.Group>

              {error && (
                <div className="alert alert-danger" role="alert">
                  <h6 className="alert-heading">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    Upload Error
                  </h6>
                  <hr />
                  <div className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                    {error}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="alert alert-success" role="alert">
              <h6 className="alert-heading">
                <i className="fa fa-check-circle me-2"></i>
                Upload Successful!
              </h6>
              <hr />
              <p className="mb-0">
                {typeof uploadResult === 'string' 
                  ? uploadResult 
                  : uploadResult.message || 'Questions have been successfully uploaded to the assessment.'
                }
              </p>
            </div>
          )}
        </>
      }
      footerContent={
        <>
          {!uploadResult ? (
            <>
              <Button
                variant="secondary"
                onClick={onHide}
                style={{ marginRight: "10px" }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={!file || isUploading}
              >
                {isUploading ? "Uploading..." : "Upload Questions"}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={handleUploadAnother}
                style={{ marginRight: "10px" }}
              >
                Upload Another
              </Button>
              <Button variant="primary" onClick={onHide}>
                Close
              </Button>
            </>
          )}
        </>
      }
    />
  );
};

export default AssessmentUploadModal;
